import { useApi } from '../../../core/api/api.context';
import { Query, QueryKey, useMutation, useQuery, useQueryClient } from 'react-query';
import { QueryKeys } from '../../../core/queries/query-keys.constants';
import {
  CanIOpenDeliveryContainerDto,
  CreateNoteDto,
  OpenLockerResponseDto,
  OrdersActionsControllerDeliverViaCloudResponse,
  OrdersActionsControllerSwitchSpaceResponse,
  ResendNotificationDto,
  TimelineDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { useTimelineUpdatesEventCentre } from '../../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import { useCallback, useMemo } from 'react';
import {
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionCallback,
  TimelineUpdatesSubscriptionStream
} from '../../../events/timelineUpdates/timelineUpdates.types';

export const useQueryGetOrderTimeline = (retailerID: string | undefined, orderId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  const query = useQuery<TimelineDto | undefined, RestError>(
    [QueryKeys.ORDERS, retailerID, orderId, QueryKeys.TIMELINE],
    async () => {
      if (retailerID === undefined) {
        return undefined;
      }
      return await api.ordersController.ordersTimeline(retailerID, orderId);
    },
    {
      keepPreviousData: true
    }
  );

  // Subscribe to timeline update events
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    // Invalidate the orders query on any timeline event so that if we navigate
    // back to the orders list, we see the updated status
    queryClient.invalidateQueries([QueryKeys.ORDERS]);
  }, [queryClient]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      return event.data.workflowID === orderId;
    },
    [orderId]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [],
      workflowIDs: [orderId],
      projectIDs: []
    }),
    [orderId]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  return query;
};

export const useMutationOpenLocker = (retailerID: string, orderID: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  function invalidationPredicate(query: Query<unknown, unknown, unknown, QueryKey>) {
    return query.queryKey[0] === QueryKeys.ORDERS && query.queryKey[3] === QueryKeys.TIMELINE;
  }

  return useMutation<OpenLockerResponseDto, RestError, string[] | undefined>(
    async (doorIDs) => {
      return (await api.ordersActionsController.ordersOpenLocker(retailerID, orderID, {
        doorIDs
      })) as unknown as Promise<OpenLockerResponseDto>;
    },
    {
      onSettled() {
        queryClient.invalidateQueries({ predicate: invalidationPredicate });
      }
    }
  );
};

export const useMutationCancelOrder = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { retailerID: string; orderID: string }>(
    async ({ orderID, retailerID }) => {
      await api.ordersActionsController.ordersCancel(retailerID, orderID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
      }
    }
  );
};

export const useMutationUndoCancellation = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { retailerID: string; orderID: string }>(
    async ({ retailerID, orderID }) => {
      await api.ordersActionsController.ordersUndoCancellation(retailerID, orderID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
      }
    }
  );
};

export const useMutationMarkOrderCollected = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { retailerID: string; orderID: string }>(
    async ({ retailerID, orderID }) => {
      await api.ordersActionsController.ordersMarkCollected(retailerID, orderID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export const useMutationMarkOrderCancelled = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { retailerID: string; orderID: string }>(
    async ({ retailerID, orderID }) => {
      await api.ordersActionsController.ordersCancel(retailerID, orderID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export const useMutationRequestRetrieval = (retailerID: string, orderID: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError>(
    async () => {
      await api.ordersActionsController.ordersRequestRetrieval(retailerID, orderID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export const useMutationMarkOrderRetrieved = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { retailerID: string; orderID: string }>(
    async ({ retailerID, orderID }) => {
      await api.ordersActionsController.ordersMarkRetrieved(retailerID, orderID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export function useMutationResendOrderNotification(retailerID: string, orderID: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    async (request: ResendNotificationDto) => {
      await api.ordersActionsController.ordersManualDeliveryNotification(retailerID, orderID, request);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.ORDERS, retailerID, orderID, QueryKeys.TIMELINE]);
      }
    }
  );
}

export const useMutationArchiveOrder = (retailerID: string, orderID: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError>(
    async () => {
      await api.ordersController.ordersDelete(retailerID, orderID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
      }
    }
  );
};

export const useMutationHandOffDeliverViaCloud = (retailerID: string, orderID: string) => {
  const api = useApi();
  return useMutation(async (kioskID: string) => {
    return await api.ordersActionsController.handoffDeliveryRequest(retailerID, orderID, { kioskID });
  });
};

export const useMutationDeliverViaCloud = (retailerID: string, orderID: string) => {
  const api = useApi();

  return useMutation<
    OrdersActionsControllerDeliverViaCloudResponse,
    RestError,
    { hubID: string; deliveryContainerID: string }
  >(async ({ hubID, deliveryContainerID }) => {
    return await api.ordersActionsController.deliverViaCloud(retailerID, orderID, {
      hubID,
      deliveryContainerID
    });
  });
};

export const useMutationRetrieveViaCloud = (retailerID: string, orderID: string) => {
  const api = useApi();
  return useMutation(async (kioskID: string) => {
    return await api.ordersActionsController.handoffRetrievalRequest(retailerID, orderID, { kioskID });
  });
};

export const useMutationSwitchSpace = (retailerID: string, orderID: string) => {
  const api = useApi();

  return useMutation<
    OrdersActionsControllerSwitchSpaceResponse,
    RestError,
    { hubID: string; deliveryContainerID: string }
  >(async ({ hubID, deliveryContainerID }) => {
    return await api.ordersActionsController.switchSpace(retailerID, orderID, {
      hubID,
      spaceID: deliveryContainerID
    });
  });
};

export const useMutationAddNoteToOrder = (orderId: string, retailerId: string, content: CreateNoteDto) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      await api.ordersNotesController.create(orderId, retailerId, content);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.ORDERS);
        queryClient.invalidateQueries(QueryKeys.ADD_NOTE);
      }
    }
  );
};

export function useQueryCanIOpenOrderLocker(retailerId: string, orderID: string) {
  const api = useApi();

  return useQuery<CanIOpenDeliveryContainerDto, RestError>(
    [QueryKeys.ORDERS, orderID, QueryKeys.CAN_I_OPEN_LOCKER],
    async () => {
      return await api.ordersActionsController.ordersCanIOpen(retailerId, orderID);
    }
  );
}
